import { parseNum } from './numero.js';
import { custoConfig } from './custos.js';
/* parametros.ts — a ÚNICA fronteira entre o banco e o motor.
 *
 * Depois desta função nenhum cálculo desconfia de nada: todo campo é number,
 * toda base é válida, todo custo já está resolvido em reais. É aqui que mora
 * TODO o conhecimento sobre o formato das colunas jsonb — nenhuma tela remonta
 * isso à mão, e é por isso que mudar o formato do banco custa um arquivo.
 */
/** As cinco linhas fixas de custo direto, na ordem em que a tela as mostra. */
const CHAVES_CUSTO = ['ca', 'inst', 'proj', 'homol', 'art'];
/**
 * Os doze meses do calendário, e SÓ eles.
 *
 * Iterar `Object.keys(meses)` somava qualquer chave que estivesse no jsonb,
 * enquanto `distribuicaoMensal` só conhece m01..m12: uma chave estranha entrava
 * no VOLUME anual e ficava fora da CURVA, e as duas discordavam dentro da mesma
 * análise. A proteção existia só na porta HTTP, no caminho de ESCRITA, então
 * não cobria dado legado nem outro caminho de gravação. O motor é quem tem de
 * saber quais meses existem.
 */
const CHAVES_MES = [
    'm01', 'm02', 'm03', 'm04', 'm05', 'm06',
    'm07', 'm08', 'm09', 'm10', 'm11', 'm12',
];
/**
 * Normaliza a base de incidência, com o padrão do CAMPO quando ela não veio.
 *
 * O padrão é por campo porque a tela é por campo, e antes não era: `base()`
 * mandava tudo que não fosse 'servico' para 'total', enquanto a tela de
 * Despesas nasce com tributo em SERVIÇO e comissão em TOTAL (os `useState` de
 * base em despesas.tsx). Registro com tributo gravado sem a chave `base` era lido pelo motor
 * como 'total' e mostrado pela tela como 'servico'.
 *
 * O tamanho do erro: a diferença é `pctTributo × kit / denominador`, e não
 * depende de custo nem de despesa fixa, ou seja escala com o KIT. No
 * residencial medido são R$ 1.558,44 por projeto; num comercial com kit de
 * R$ 380 mil e 8 pontos de tributo, R$ 39.480,52.
 *
 * Só valor DESCONHECIDO cai no padrão. Escolha explícita do usuário, inclusive
 * a que coincide com o padrão, continua sendo respeitada como está gravada.
 */
export function base(valor, padrao) {
    if (valor === 'servico' || valor === 'total')
        return valor;
    return padrao;
}
/**
 * O padrão de cada campo, num lugar só. Exportado porque a tela de Despesas e
 * o painel de administração precisam DIZER a mesma base que o motor calcula;
 * três cópias do mesmo par de constantes é como a divergência acima nasceu.
 */
export const BASE_PADRAO = {
    comissao: 'total',
    tributo: 'servico',
    extra: 'total',
};
/**
 * Um override de projeto só vale se for NUMBER.
 *
 * Por quê: `0` é override legítimo (o usuário zerou o campo de propósito) e
 * `undefined` é ausência, então não dá para usar truthiness. E string NÃO conta
 * — se alguma tela gravar "1000", o valor cai na configuração da empresa em vez
 * de virar mil reais. É um contrato frágil e conhecido; hoje todas as telas
 * gravam number (passam por parseNum antes do insert).
 *
 * `Number.isFinite` em vez de só `typeof === 'number'`: NaN é typeof number e
 * contaminaria o CDT inteiro — jsonb não representa NaN, então o guarda é
 * inalcançável pelo banco e de graça contra um chamador desatento.
 */
function override(valor) {
    return typeof valor === 'number' && Number.isFinite(valor) ? valor : null;
}
/**
 * montarParametros({integrador, projeto}): traduz as duas linhas do banco no
 * vetor de entrada do cálculo.
 *
 * `projeto` é opcional de propósito: as telas de diagnóstico (Indicadores,
 * Despesas) calculam o cenário da EMPRESA, sem projeto nenhum — nesse caso o
 * ticket vem do histórico e as medidas do sistema são 0.
 */
export function montarParametros(fonte) {
    const integ = fonte.integrador ?? {};
    const proj = fonte.projeto ?? null;
    const configs = integ.configs?.campos ?? {};
    const extrasCfg = integ.configs?.extras ?? [];
    const kwp = proj ? parseNum(proj.kwp_sistema) : 0;
    const mods = proj ? parseNum(proj.quantidade_modulos) : 0;
    const overrides = proj?.custos_diretos ?? {};
    // ── Custos diretos nominais ────────────────────────────────────────────
    // Precedência: ajuste do projeto → configuração da empresa → 0.
    // Zero de verdade: custo não configurado NÃO entra com valor fantasma no
    // preço. Inventar um número aqui é pior que faltar — o usuário confiaria nele.
    const custos = {};
    for (const k of CHAVES_CUSTO) {
        const ov = override(overrides[k]);
        if (ov !== null) {
            custos[k] = ov;
            continue;
        }
        custos[k] = custoConfig(configs[k], kwp, mods) ?? 0;
    }
    // ── KIT ────────────────────────────────────────────────────────────────
    // Uma fonte só: o valor digitado neste projeto. O kit é faturado pela
    // distribuidora e sai do orçamento de cada venda, então não existe padrão de
    // empresa para cair — a linha configurável que existia aqui foi removida
    // porque taxa por kWp não cota kit, só preenche o campo com número inventado.
    const kit = proj ? parseNum(proj.custo_kit) : 0;
    // ── Custos extras da empresa (com override por projeto) ────────────────
    const ovExtras = overrides.extras ?? {};
    const extras = [];
    for (const e of extrasCfg) {
        // Sem rótulo = linha que o usuário abriu no formulário e não preencheu.
        // Cobrar por ela seria cobrar por um custo sem nome no relatório.
        if (!e || !e.label)
            continue;
        const id = e.id ?? '';
        const ov = override(ovExtras[id]);
        const valor = ov !== null ? ov : custoConfig(e, kwp, mods) ?? 0;
        extras.push({ id, label: e.label, valor });
    }
    // ── Despesas variáveis ─────────────────────────────────────────────────
    const d = integ.despesas ?? {};
    const despesasExtras = (d.extras ?? []).map((e) => ({
        nome: e?.nome || 'Extra',
        valor: parseNum(e?.valor),
        // Extra nasce em TOTAL na tela (despesas.tsx).
        base: base(e?.base, BASE_PADRAO.extra),
    }));
    // ── Volume anual ───────────────────────────────────────────────────────
    // Sazonalidade preenchida ganha; senão projetos/mês × 12.
    //
    // Cobertura parcial: o histórico não precisa ter 12 meses. `cobertura`
    // (1..12, default 12 para dados antigos) diz quantos meses os números
    // representam, e o volume é ANUALIZADO: 10 projetos em 6 meses → 20/ano.
    // O ticket médio NÃO anualiza — é média POR PROJETO, independe do período.
    // Confundir os dois foi um bug real: o ticket dobrava junto com o volume.
    let totalProjetos = 0;
    let totalValor = 0;
    let meses = null;
    if (integ.sazonalidade?.meses) {
        meses = integ.sazonalidade.meses;
        for (const chave of CHAVES_MES) {
            const mes = meses[chave];
            totalProjetos += parseNum(mes?.projetos);
            totalValor += parseNum(mes?.valor);
        }
    }
    // `|| 12` e não `??`: cobertura 0 (campo limpo) tem de virar 12, não 0 —
    // dividir por 0 aqui produziria Infinity no volume.
    const cobertura = Math.min(12, Math.max(1, parseNum(integ.sazonalidade?.cobertura) || 12));
    const projetosMes = parseNum(integ.projetos_mes);
    const V12 = totalProjetos > 0
        ? (totalProjetos / cobertura) * 12
        : projetosMes > 0
            ? projetosMes * 12
            : 0;
    // Exige os dois > 0: sem projetos o ticket médio seria divisão por zero, e
    // sem valor seria R$ 0 por projeto — um "dado" que faria o preço despencar.
    const ticketMedioSaz = totalProjetos > 0 && totalValor > 0 ? totalValor / totalProjetos : 0;
    const valorVenda = proj ? parseNum(proj.valor_venda) : 0;
    const cdt = kit +
        custos.ca +
        custos.inst +
        custos.proj +
        custos.homol +
        custos.art +
        extras.reduce((s, e) => s + e.valor, 0);
    return {
        kit,
        custos,
        extras,
        cdt,
        ticket: valorVenda > 0 ? valorVenda : ticketMedioSaz,
        ticketOrigem: valorVenda > 0 ? 'projeto' : ticketMedioSaz > 0 ? 'sazonalidade' : 'nenhum',
        comissao: parseNum(d.comissao?.valor),
        comissaoBase: base(d.comissao?.base, BASE_PADRAO.comissao),
        tributo: parseNum(d.tributo?.valor),
        tributoBase: base(d.tributo?.base, BASE_PADRAO.tributo),
        outras: parseNum(d.outras),
        despesasExtras,
        despFixa: parseNum(d.despFixa),
        // `== null` cobre null e undefined: meta nunca definida vira o padrão 12.
        // Mas lucro_alvo = 0 é uma ESCOLHA (trabalhar no custo) e tem de sobreviver.
        lucroAlvo: integ.lucro_alvo == null ? 12 : parseNum(integ.lucro_alvo),
        lucroAlvoDefinido: integ.lucro_alvo != null,
        V12,
        meses,
        ticketMedioSaz,
        coberturaSaz: cobertura,
        temSazonalidade: totalProjetos > 0,
        kwp,
        mods,
    };
}
//# sourceMappingURL=parametros.js.map